import { elementType, ValidationMatrix } from '../models/validation-matrix.model';
import { Validators } from '@angular/forms';
import { decimalLimitValidator } from '../validators/decimal-limit.validator';

export class DataCollectionValidation {
  static numbersOnly = /^[0-9]*\.?[0-9]+$/;

  public static CollectingOrganisation: ValidationMatrix = {
    fieldName: 'Collecting Organisation',
    elementType: elementType.Dropdown,
    validators: [Validators.required],
    validationDetails: {
      requiredDetail: {
        fieldMessage: 'Select the organisation that collected the survey data.',
        bannerMessage: 'Select the organisation that collected the survey data.'
      }
    }
  };

  public static SurveyPlatform: ValidationMatrix = {
    fieldName: 'Name of survey platform',
    elementType: elementType.Input,
    validators: [Validators.required, Validators.maxLength(150)],
    validationDetails: {
      requiredDetail: {
        fieldMessage: 'Enter the name of the survey platform.',
        bannerMessage: 'Enter the name of the survey platform.'
      },
      maxLengthDetail: {
        fieldMessage: `Name of the survey platform must be 150 characters or less.`,
        bannerMessage: 'Name of the survey platform must be 150 characters or less.',
        maxLength: 150
      }
    }
  };

  public static InstrumentMakeAndModel: ValidationMatrix = {
    fieldName: 'Instrument make and model',
    elementType: elementType.Input,
    validators: [Validators.required, Validators.maxLength(150)],
    validationDetails: {
      requiredDetail: {
        fieldMessage: 'Enter the instrument make and model.',
        bannerMessage: 'Enter the instrument make and model.'
      },
      maxLengthDetail: {
        fieldMessage: `Instrument make and model must be 150 characters or less.`,
        bannerMessage: 'Instrument make and model must be 150 characters or less.',
        maxLength: 150
      }
    }
  };

  public static PositioningSystem: ValidationMatrix = {
    fieldName: 'Positioning System',
    elementType: elementType.Input,
    validators: [Validators.required, Validators.maxLength(200)],
    validationDetails: {
      requiredDetail: {
        fieldMessage: 'Enter the positioning system.',
        bannerMessage: 'Enter the positioning system.'
      },
      maxLengthDetail: {
        fieldMessage: `Positioning system must be 200 characters or less.`,
        bannerMessage: 'Positioning system must be 200 characters or less. ',
        maxLength: 200
      }
    }
  };

  public static MethodOfSurvey: ValidationMatrix = {
    fieldName: 'Method of survey',
    elementType: elementType.Dropdown,
    validators: [Validators.required, Validators.maxLength(100)],
    validationDetails: {
      requiredDetail: {
        fieldMessage: 'Select the method of survey.',
        bannerMessage: 'Select the method of survey.'
      }
    }
  };

  public static ConductedCrosslines: ValidationMatrix = {
    fieldName: 'Conducted crosslines',
    elementType: elementType.Radio,
    validators: [Validators.required],
    validationDetails: {
      requiredDetail: {
        fieldMessage: 'Confirm if you have conducted crosslines on the survey.',
        bannerMessage: 'Confirm if you have conducted crosslines on the survey.'
      }
    }
  };

  public static GriddedResolution: ValidationMatrix = {
    fieldName: 'Gridded resolution',
    elementType: elementType.Input,
    validators: [
      Validators.required,
      Validators.pattern(this.numbersOnly),
      decimalLimitValidator(8, 2),
      Validators.maxLength(11)
    ],
    validationDetails: {
      maxLengthDetail: {
        fieldMessage: `Gridded resolution must be 8 digits or less.`,
        bannerMessage: 'Gridded resolution must be 8 digits or less.'
      },
      requiredDetail: {
        fieldMessage: 'Enter the gridded resolution.',
        bannerMessage: 'Enter the gridded resolution.'
      },
      decimalLimitDetail: {
        fieldMessage: 'Gridded resolution must have 2 decimal points or less.',
        bannerMessage: 'Gridded resolution must have 2 decimal points or less.'
      },
      limitExceededDetail: {
        fieldMessage: `Gridded resolution must be 8 digits or less.`,
        bannerMessage: 'Gridded resolution must be 8 digits or less.'
      },
      patternDetail: {
        fieldMessage: `Gridded resolution must be 8 digits or less.`,
        bannerMessage: 'Gridded resolution must be 8 digits or less.'
      }
    }
  };

  public static GriddedDataSupplied: ValidationMatrix = {
    fieldName: 'Gridded data supplied',
    elementType: elementType.Radio,
    validators: [Validators.required],
    validationDetails: {
      requiredDetail: {
        fieldMessage: 'Confirm if you are supplying thinned or gridded data.',
        bannerMessage: 'Confirm if you are supplying thinned or gridded data.'
      }
    }
  };

  public static MethodUsed: ValidationMatrix = {
    fieldName: 'Method used',
    elementType: elementType.Input,
    validators: [Validators.required, Validators.maxLength(200)],
    validationDetails: {
      requiredDetail: {
        fieldMessage: 'Select the gridding method used.',
        bannerMessage: 'Select the gridding method used.'
      },
      maxLengthDetail: {
        fieldMessage: `Method used must be 200 characters or less.`,
        bannerMessage: 'Method used must be 200 characters or less.',
        maxLength: 200
      }
    }
  };

  public static MethodUsedOther: ValidationMatrix = {
    fieldName: 'Method used other',
    elementType: elementType.Input,
    validators: [Validators.required, Validators.maxLength(400)],
    validationDetails: {
      requiredDetail: {
        fieldMessage: 'Enter more information about gridding method used when selecting Other.',
        bannerMessage: 'Enter more information about gridding method used.'
      },
      maxLengthDetail: {
        fieldMessage: `Information about gridding method used must be 400 characters or less.`,
        bannerMessage: 'Information about gridding method used must be 400 characters or less.',
        maxLength: 400
      }
    }
  };

  public static DataInterpolated: ValidationMatrix = {
    fieldName: 'Data Interpolated',
    elementType: elementType.Radio,
    validators: [Validators.required],
    validationDetails: {
      requiredDetail: {
        fieldMessage: 'Confirm if the data has been interpolated.',
        bannerMessage: 'Confirm if the data has been interpolated.'
      }
    }
  };

  public static DataInterpolatedOther: ValidationMatrix = {
    fieldName: 'Data Interpolated Other',
    elementType: elementType.Input,
    validators: [Validators.maxLength(400)],
    validationDetails: {
      requiredDetail: {
        fieldMessage: 'Enter more information about interpolated data when selecting Yes.',
        bannerMessage: 'Enter more information about interpolated data.'
      },
      maxLengthDetail: {
        fieldMessage: `Information about interpolated data must be 400 characters or less.`,
        bannerMessage: 'Information about interpolated data must be 400 characters or less.',
        maxLength: 400
      }
    }
  };

  public static CoordinateType: ValidationMatrix = {
    fieldName: 'Coordinate Type',
    elementType: elementType.Radio,
    validators: [Validators.required],
    validationDetails: {
      requiredDetail: {
        fieldMessage: 'Select the coordinate type.',
        bannerMessage: 'Select the coordinate type.'
      }
    }
  };

  public static HorizontalDatum: ValidationMatrix = {
    fieldName: 'Horizontal Datum',
    elementType: elementType.Dropdown,
    validators: [Validators.required],
    validationDetails: {
      requiredDetail: {
        fieldMessage: 'Select the horizontal datum.',
        bannerMessage: 'Select the horizontal datum.'
      }
    }
  };

  public static HorizontalDatumOther: ValidationMatrix = {
    fieldName: 'Horizontal Datum Other',
    elementType: elementType.Input,
    validators: [Validators.maxLength(100)],
    validationDetails: {
      requiredDetail: {
        fieldMessage: 'Enter more information about the horizontal datum when selecting Other.',
        bannerMessage: 'Enter more information about the horizontal datum.'
      },
      maxLengthDetail: {
        fieldMessage: `Information about the horizontal datum must be 100 characters or less.`,
        bannerMessage: 'Information about the horizontal datum must be 100 characters or less.',
        maxLength: 100
      }
    }
  };

  public static VerticalDatum: ValidationMatrix = {
    fieldName: 'Vertical Datum',
    elementType: elementType.Dropdown,
    validators: [Validators.required],
    validationDetails: {
      requiredDetail: {
        fieldMessage: 'Select the vertical datum.',
        bannerMessage: 'Select the vertical datum.'
      }
    }
  };

  public static VerticalDatumOther: ValidationMatrix = {
    fieldName: 'Vertical Datum Other',
    elementType: elementType.Input,
    validators: [Validators.maxLength(100)],
    validationDetails: {
      requiredDetail: {
        fieldMessage: 'Enter more information about the vertical datum when selecting Other or Local.',
        bannerMessage: 'Enter more information about the vertical datum.'
      },
      maxLengthDetail: {
        fieldMessage: `Information about the vertical datum must be 100 characters or less.`,
        bannerMessage: 'Information about the vertical datum must be 100 characters or less.',
        maxLength: 100
      }
    }
  };

  public static Projection: ValidationMatrix = {
    fieldName: 'Projection',
    elementType: elementType.Input,
    validators: [Validators.maxLength(1024)],
    validationDetails: {
      maxLengthDetail: {
        fieldMessage: `Information about projection data must be 1,024 characters or less.`,
        bannerMessage: 'Information about projection must be 1,024 characters or less.',
        maxLength: 1024
      }
    }
  };
}
